import { useEffect, useMemo, useRef, useState } from "react";
import "@styles/sections/EventSection.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  useMediaQuery,
  Dialog,
  Select,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import DropdownFilterMenu from "@components/ui/DropdownFilterMenu";
import { useNavigate } from "react-router-dom";
import { resetEventForm, setEventGaType } from "@store/eventForm/eventFormSlice";
import { getOptionData, getLocationByType } from "@api/commonApi";
import { getEventList } from "@api/landingApi";
import {
  workshopFilterData,
  genericLatestPopularFilterData,
} from "@data/FilterData";

import { DateRangePicker } from "react-date-range";
import { zhHK } from "date-fns/locale";
import { enUS } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import SortIcon from "@mui/icons-material/Sort";
import { gaEvent } from "@utils/gaEvent";

const EventSection = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isChinese = i18n.language === "hk";
  const isPad = useMediaQuery("(max-width:768px)");
  const isMobile = useMediaQuery("(max-width:425px)");

  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );

  // ====== 搬 EventSummaryPage：狀態 ======
  const datePickerRef = useRef(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    { startDate: null, endDate: null, key: "selection" },
  ]);

  const [filterOptions, setFilterOptions] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [cardList, setCardList] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  // 與 Summary 一樣的 filter list
  const filterList = useMemo(() => workshopFilterData, []);



const getFilteredOptions = (filterType) => {
    const options = filterOptions[filterType] || [];

    if (filterType === "workshop_product") {
      const currentTheme = selectedValues.workshop_theme;
      
      if (String(currentTheme) === "1") {
        // Show only products 1, 2, 3, 4
        return options.filter((opt) => [1, 2, 3, 4].includes(Number(opt.value)));
      }
      if (String(currentTheme) === "6") {
        // Show only products 5, 6
        return options.filter((opt) => [5, 6].includes(Number(opt.value)));
      }
      if (String(currentTheme) === "0") {
        // ✅ Show only product 0
        return options.filter((opt) => [0,1,2,3,4,5,6].includes(Number(opt.value)));
      }
    }

    return options;
  };

  // Automatically adjust or reset the workshop_product when the theme changes
  useEffect(() => {
    const currentTheme = selectedValues.workshop_theme;
    const currentProduct = selectedValues.workshop_product;

    if (String(currentTheme) === "0") {
      // ✅ If theme is 0, automatically select product 0
      if (Number(currentProduct) !== 0) {
        setSelectedValues((prev) => ({ ...prev, workshop_product: 0 }));
      }
    } else if (currentProduct !== undefined && currentProduct !== null) {
      // Reset invalid product selections for other themes
      if (String(currentTheme) === "1" && ![1, 2, 3, 4].includes(Number(currentProduct))) {
        setSelectedValues((prev) => ({ ...prev, workshop_product: null }));
      } else if (String(currentTheme) === "6" && ![5, 6].includes(Number(currentProduct))) {
        setSelectedValues((prev) => ({ ...prev, workshop_product: null }));
      }
    }
  }, [selectedValues.workshop_theme]);


  // ====== 拉 Event List（與 Summary 同步）======
  useEffect(() => {
    (async () => {
      try {
        const res = await getEventList();
        setCardList(res.data?.Data || []);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  // ====== 拉所有 options（與 Summary 同步）======
  useEffect(() => {
    const fetchAllOptions = async () => {
      const result = {};

      for (const filter of filterList) {
        try {
          let res;
          if (filter.type === "workshop_location") {
            res = await getLocationByType({ type: 2 }); // 特殊 API
          } else {
            res = await getOptionData({ type: filter.type });
          }
          result[filter.type] = res?.data?.Data || [];
        } catch (err) {
          console.error("Failed to fetch", filter.type, err);
        }
      }

      setFilterOptions(result);
    };

    fetchAllOptions();
  }, [filterList]);

  // ====== 點擊外面關閉 DatePicker（與 Summary 同步）======
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
    }
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  // ====== filter 改變（與 Summary 同步）======
  const handleFilterChange = (type, selectedItem) => {
    const field =
      filterList.find((f) => f.type === type)?.valueField || "value";

    setSelectedValues((prev) => ({
      ...prev,
      [type]: selectedItem?.[field],
    }));

    console.log(`Filter ${type} changed:`, selectedItem);
  };

  // ====== 過濾 + 排序（與 Summary 同步）======
  const filteredCards = cardList.filter((item) => {
    // 1. Product
    if (selectedValues.workshop_product) {
      const product = item.product?.split(",") || [];
      if (!product.includes(String(selectedValues.workshop_product))) {
        return false;
      }
    }

    // 2. Theme
    if (selectedValues.workshop_theme) {
      const themes = item.theme?.split(",") || [];
      if (!themes.includes(String(selectedValues.workshop_theme))) {
        return false;
      }
    }

    // 3. Location
    if (selectedValues.workshop_location) {
      const locations = item.location_id?.split(",") || [];
      if (!locations.includes(String(selectedValues.workshop_location))) {
        return false;
      }
    }

    // 4. 日期範圍：start_date 是否在選擇區間
    const toDayStart = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const toDayEnd = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

    // 4. 日期範圍 filter：與用戶所選 dateRange 是否有交集（inclusive）
    if (dateRange[0].startDate && dateRange[0].endDate) {
      const cardStart = toDayStart(new Date(item.start_date)); // e.g. "2025-09-18"
      const cardEnd = toDayEnd(new Date(item.end_date || item.start_date)); // 沒有 end 就用 start 當單日課程

      const filterStart = toDayStart(new Date(dateRange[0].startDate)); // e.g. "2025-09-20"
      const filterEnd = toDayEnd(new Date(dateRange[0].endDate)); // e.g. "2025-09-26"

      const hasOverlap = cardStart <= filterEnd && cardEnd >= filterStart;
      if (!hasOverlap) return false;
    }

    // 5. 會員限定（boolean）
    if (selectedValues.member_privilege === true) {
      const isMemberOnly = String(item.type) === "3";
      if (!isMemberOnly) return false;
    }

    return true;
  });

  const sortValue = Number(selectedValues.latest_most_popular);
  const sortedCards = [...filteredCards].sort((a, b) => {
    if (sortValue === 1) {
      return (b.total_enrollment || 0) - (a.total_enrollment || 0); // 最熱門
    }
    return Number(b.id) - Number(a.id); // 最新
  });

  // ====== 重置全部（與 Summary 同步）======
  const handleResetAllFilters = () => {
    setSelectedValues({});
    setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
  };

  return (
    <Box className="event-section-container">
      <Typography variant="h4" className="title">
        <Trans i18nKey="generic:event" />
      </Typography>

      {isMobile ? (
        <>
          <Box className="dialog-mobile-filter-button-container">
            <Button
              className="dropdown-button mobile-filter-button"
              onClick={() => setMobileOpen(true)}
            >
              <FilterAltIcon />
              <Trans i18nKey="generic:filter" />
            </Button>
          </Box>
          <Dialog
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            fullScreen
            PaperProps={{ className: "dialog-filter-container" }}
            sx={{
              "& .MuiDialog-paper": {
                position: "fixed",
                bottom: 0,
                maxHeight: "88vh",
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                border: "2px solid #f5f5f5",
                background: "#fbfbfb",
              },
            }}
          >
            <Box className="dialog-top-container">
              <Typography className="dialog-title">
                <Trans i18nKey="generic:event" />
              </Typography>
              <CloseIcon onClick={() => setMobileOpen(false)} />
            </Box>

            <Box className="dialog-content">
              <Typography className="dialog-text">
                <SortIcon className="dialog-icon" />
                <Trans i18nKey="generic:sort" />
              </Typography>
              <Box className="toggle-button-container">
                <ToggleButtonGroup
                  value={selectedValues.latest_most_popular ?? 0}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleFilterChange("latest_most_popular", {
                        value: newValue,
                      });
                    }
                  }}
                >
                  <ToggleButton value={0}>
                    <Trans i18nKey="generic:most_latest" />
                  </ToggleButton>
                  <ToggleButton value={1}>
                    <Trans i18nKey="generic:most_popular" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box className="dialog-line"></Box>
              <Typography className="dialog-text">
                <FilterAltIcon className="dialog-icon" />
                <Trans i18nKey="generic:filter" />
              </Typography>
              {filterList.map((filter) => {
                const langKey = filter.displayField?.[i18n.language];
                const valueField = filter.valueField || "value";
                //const options = filterOptions[filter.type] || [];
                const options = getFilteredOptions(filter.type);

                // 預設值：有 selectedValues 就用，否則取第一個 option 嘅 value
                const currentValue =
                  selectedValues?.[filter.type] ??
                  (options.length > 0 ? options[0][valueField] : "");

                return (
                  <Box className="select-container">
                    <Select
                      className="select-filter"
                      variant="outlined"
                      key={filter.type}
                      value={currentValue}
                      onChange={(e) => {
                        const v = e.target.value;
                        const selectedItem =
                          options.find(
                            (o) => String(o?.[valueField]) === String(v)
                          ) || null;
                        handleFilterChange(filter.type, selectedItem);
                      }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt[valueField]} value={opt[valueField]}>
                          {langKey
                            ? opt[langKey]
                            : opt.label || opt.name || String(opt[valueField])}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                );
              })}
              <Box className="member-privilege-switch-container">
                <PeopleAltIcon
                  className={
                    selectedValues.member_privilege && "selected_member"
                  }
                />
                <Typography>
                  <Trans i18nKey="generic:member_exclusive" /> :
                </Typography>
                <Switch
                  className="member-privilege-switch"
                  checked={!!selectedValues.member_privilege}
                  onChange={(e) =>
                    setSelectedValues((prev) => ({
                      ...prev,
                      member_privilege: e.target.checked,
                    }))
                  }
                  onClick={(e) => e.stopPropagation()} // ✅ 防止重複觸發 Button onClick
                />
              </Box>
              <Box className="dialog-date-range-picker-container">
                <Typography className="date-picker-title">
                  <Trans i18nKey="form:mobile_select_date_range" /> :
                </Typography>
                <DateRangePicker
                  ranges={dateRange}
                  onChange={(ranges) => setDateRange([ranges.selection])}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  direction="horizontal"
                  showMonthAndYearPickers={true}
                  locale={isChinese ? zhHK : enUS}
                  showDateDisplay={true} // ❌ 隱藏 header 顯示
                  startDatePlaceholder={t("form:start_date")}
                  endDatePlaceholder={t("form:end_date")}
                  showPreview={true} // ❌ 不顯示 hover 預覽
                  staticRanges={[]} // ❌ 移除左側快捷範圍
                  inputRanges={[]} // ❌ 移除下方自訂範圍
                  minDate={new Date("2025-01-01")} // 最早可選
                  maxDate={new Date("2027-12-01")} // 最遲可選
                  rangeColors={["#f5b623"]}
                  dateDisplayFormat="yyyy-MM-dd"
                />
              </Box>
              <Box className="dialog-line"></Box>
              <Box className="dialog-button-container">
                <Button
                  className="button dialog-reset-filter-button"
                  onClick={handleResetAllFilters}
                >
                  <Trans i18nKey="generic:reset" />
                  <RestartAltIcon className="reset-icon" />
                </Button>
                <Button
                  className="loading-button"
                  onClick={() => setMobileOpen(false)}
                >
                  <Trans i18nKey="generic:view_results" />
                </Button>
              </Box>{" "}
            </Box>
          </Dialog>
        </>
      ) : (
        <>
          {/* ====== Filter 區域：硬搬 Summary 的 UI（小調整）====== */}
          <Box className="filter-container">
            {filterList.map((filter) => (
              <DropdownFilterMenu
                key={filter.type}
                options={getFilteredOptions(filter.type)}
                displayField={filter.displayField[i18n.language]}
                valueField={filter.valueField}
                lang={i18n.language}
                value={selectedValues[filter.type]}
                onChange={(item) => handleFilterChange(filter.type, item)}
              />
            ))}

            {/* 日期範圍 */}
            <Button
              className="dropdown-button"
              variant="outlined"
              onClick={() => setShowDatePicker((s) => !s)}
            >
              <Trans i18nKey="form:select_date_range" />
              {dateRange[0].startDate && dateRange[0].endDate && (
                <RestartAltIcon
                  className="reset-date-button"
                  onClick={() =>
                    setDateRange([
                      { startDate: null, endDate: null, key: "selection" },
                    ])
                  }
                />
              )}
            </Button>

            {/* 會員限定 checkbox（與 Summary 同款） */}
            <Button
              className="dropdown-button"
              onClick={() =>
                setSelectedValues((prev) => ({
                  ...prev,
                  member_privilege: !prev.member_privilege, // 反轉
                }))
              }
            >
              <span className="member-privilege-label">
                <PeopleAltIcon />
                <Trans i18nKey="generic:member_exclusive" />
              </span>
              <Checkbox
                className="member-privilege-checkbox"
                checked={!!selectedValues.member_privilege}
                onChange={(e) =>
                  setSelectedValues((prev) => ({
                    ...prev,
                    member_privilege: e.target.checked,
                  }))
                }
                onClick={(e) => e.stopPropagation()} // ✅ 防止重複觸發 Button onClick
              />
            </Button>

            {/* 最新/最熱門 */}
            <DropdownFilterMenu
              options={genericLatestPopularFilterData}
              displayField={(item) =>
                isChinese ? item.desc_hk : item.desc_hk_en
              }
              valueField="value"
              lang={i18n.language}
              value={selectedValues.latest_most_popular}
              onChange={(item) =>
                handleFilterChange("latest_most_popular", item)
              }
              icon={"sort"}
            />

            {/* 重置 */}
            <Button
              className="dropdown-button reset-filter-button"
              onClick={handleResetAllFilters}
            >
              <Trans i18nKey="generic:reset" />
              <RestartAltIcon className="reset-icon" />
            </Button>
          </Box>

          {showDatePicker && (
            <Box className="date-range-picker-container" ref={datePickerRef}>
              <DateRangePicker
                ranges={dateRange}
                onChange={(ranges) => setDateRange([ranges.selection])}
                moveRangeOnFirstSelection={false}
                months={1}
                direction="horizontal"
                showMonthAndYearPickers
                locale={isChinese ? zhHK : enUS}
                showDateDisplay={false}
                showPreview={true}
                staticRanges={[]}
                inputRanges={[]}
                minDate={new Date("2025-01-01")}
                maxDate={new Date("2027-12-01")}
                rangeColors={["#f5b623"]}
              />
            </Box>
          )}
        </>
      )}
      {/* ====== 卡片區：維持首頁 2/4/6，邏輯用 sortedCards ====== */}
      {sortedCards.length > 0 ? (
        <>
          <Box className="card-section">
            {sortedCards
              .slice(0, isMobile ? 2 : isPad ? 4 : 6)
              .map((item, index) => (
                <Box
                  className="card-container"
                  key={index}
                  onClick={() => {
                    gaEvent(
                      "clubs_homepage",
                      "galaxy_ai_academy_detail",
                      "galaxy_ai_academy_item",
                      item.id
                    );
                    
                    dispatch(resetEventForm());
                    dispatch(setEventGaType(""));
                    if (item.type == 1) {
                      navigate(`/event_form?id=${item.id}`);
                    }
                    if (item.type == 2) {
                      navigate(`/event_form?code=${item.campaign_code}`);
                    }
                    if (item.type == 3) {
                      if (memberInfoData) {
                        navigate(`/event_form?id=${item.id}`);
                      } else {
                        navigate(
                          `/login?redirect_url=/event_form?id=${item.id}`
                        );
                      }
                    }
                  }}
                >
                  <Box className="card-image-container">
                    {item.type == 3 && (
                      <>
                        <Box className="card-gradient"> </Box>
                        <Box className="card-type">
                          <PeopleAltIcon className="type-icon" />
                          <Trans i18nKey="form:member_exclusive" />
                        </Box>
                      </>
                    )}
                    <Box className="card-label">
                      <img
                        className="card-image"
                        alt={index}
                        src={
                          isChinese ? item.thumbnail_hk : item.thumbnail_hk_en
                        }
                      />
                    </Box>
                  </Box>
                  <Box className="detail-container">
                    <Typography className="detail-title">
                      {isChinese ? item.title_hk : item.title_hk_en}
                    </Typography>
                    <Typography className="description">
                      {isChinese ? item.desc_hk : item.desc_hk_en}
                    </Typography>{" "}
                    <Box className="summary-button-container">
                      <Button className="summary-button">
                        <Trans i18nKey="form:register_now" />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}
          </Box>

          <Box className="button-container">
            <Button
              className="loading-button"
              onClick={() => navigate("/galaxy_ai_academy")}
            >
              <Trans i18nKey="generic:see_more" />
            </Button>
          </Box>
        </>
      ) : (
        <Typography className="coming-soon-text" variant="h6">
          <Trans i18nKey="home:coming_soon" />
        </Typography>
      )}
    </Box>
  );
};

export default EventSection;
